<template>
    <b-form-row v-if="proposal && modules">
        <b-col cols="6" sm="5" md="2">
            <div class="proposal-back-annual-plan">
                <b-button :to="{name: 'annualPlan',
                                params: {applicationId: applicationId}
                          }"
                          class="proposal-back-title"
                >
                    <fa-icon :icon="['fal','chevron-circle-left']" />
                    Back to Annual Plan
                </b-button>
            </div>
            <br>
            <b-nav pills vertical class="shadow">
                <b-nav-item
                    :active="isActive(proposalHistoryTab.name)"
                    :class="{
                        'tab-outline': true,
                        active: isActive(proposalHistoryTab.name)
                    }"
                    @click="selectTab(proposalHistoryTab.name)"
                >
                    {{ proposalHistoryTab.description }}
                </b-nav-item>
                <b-nav-item
                    :active="isActive(proposalInfoTab.name)"
                    :class="{
                        'tab-outline': true,
                        active: isActive(proposalInfoTab.name)
                    }"
                    @click="selectTab(proposalInfoTab.name)"
                >
                    {{ proposalInfoTab.description }}
                </b-nav-item>
                <b-nav-item
                    v-for="tab in moduleTabList"
                    :key="tab.name"
                    :active="isActive(tab.name)"
                    class="tab-outline nav-item text-success"
                    @click="selectTab(tab.name)"
                >
                    <fa-icon
                        v-if="tab.statusIcon"
                        :icon="tab.statusIcon.icon"
                        :class="tab.statusIcon.classes"
                    />
                    {{ tab.description }}
                </b-nav-item>
            </b-nav>
        </b-col>
        <b-col class="col-sm-10">
            <b-card
                header-class="title-display"
                no-body
            >
                <application-header>
                    <budget-summary />
                    <div class="separator-line" />

                    <!-- <b-row>
                        <b-col cols="12"
                               class="activity-section-header"
                        >
                            <div class="activity-section-header-title">
                                Applicant:
                            </div>
                            <span class="title-display-content">
                                {{ applicantName }}
                            </span>
                            <div class="header-separator" />
                        </b-col>
                    </b-row> -->
                    <b-row class="d-flex justify-content-start align-items-stretch ml-3">
                        <div class="title-display-label">
                            Applicant:
                            <span class="title-display-content">
                                {{ applicantName }}
                            </span>
                        </div>
                        <div class="title-display-label ml-5">
                            Type:
                            <span class="title-display-content">
                                {{ proposalTypeName(proposal.ref_proposal_type_id) }}
                            </span>
                        </div>
                    </b-row>
                    <b-row class="d-flex justify-content-start align-items-stretch ml-3">
                        <div class="title-display-label">
                            Activity Title:
                            <span class="title-display-content">
                                {{ proposal.content.proposal_title }} ({{ proposal.proposal_id }})
                            </span>
                        </div>
                        <div class="title-display-label ml-5">
                            Focus Area:
                            <span class="title-display-content">
                                {{ focusAreaName || "All Areas" }}
                            </span>
                        </div>
                        <div class="title-display-label ml-5">
                            Cluster:
                            <span class="title-display-content">
                                {{ clusterName || "All Clusters" }}
                            </span>
                        </div>
                    </b-row>
                </application-header>
                <b-card-body>
                    <transition name="fade" mode="out-in">
                        <router-view :key="$route.fullPath" />
                    </transition>
                </b-card-body>
            </b-card>
        </b-col>
    </b-form-row>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';

import ApplicationHeader from '../Application/ApplicationHeader';
import BudgetSummary from '~/components/applications/perkins/ace/BudgetSummary';

export default {
    name: 'Proposal',

    components: {
        'budget-summary': BudgetSummary,
        'application-header': ApplicationHeader,
    },

    props: {
        formDisabled: { type: Boolean, default: true },
        proposalId: { type: Number, required: true },
        applicationId: { type: Number, required: true },
    },

    data() {
        return {
            proposalInfoTab: {
                id: 0,
                name: 'Info',
                description: 'Activity Info'
            },
            proposalHistoryTab: {
                id: 1,
                name: 'History',
                description: 'Activity History'
            },
            activeTab: null,
            tabIndex: 0
        };
    },

    computed: {
        ...mapGetters({
            proposal: 'proposal/proposalData',
            proposalModules: 'proposal/proposalModules',
            proposalTypeName: 'ref/application/proposalTypeName',
            proposalApprovalDate: 'proposal/proposalApprovalDate',
            generalInformation: 'coverpage/generalInformation',
            isReviewer: 'auth/isReviewer',
            isAdmin: 'auth/isAdmin',
            isModuleEditable: 'module/isModuleEditable',
            proposalData: 'proposal/proposalData',
            pathwayClusterItems: 'ref/global/pathwayClusters',
            currentAy: 'ref/global/currentAy',
        }),

        ...mapState('application', {
            academicYear: (state) => state.applicationData.academic_year,
            applicantId: (state) => state.applicationData.applicant_id,
        }),

        modules() {
            return this.proposalModules ? this.proposalModules.modules : null;
        },

        applicantName() {
            return this.generalInformation.applicant_name || 'No Applicant Name';
        },

        focusAreaName() {
            return this.proposalData.content.focus_area;
        },

        clusterName() {
            let cluster = null;
            this.pathwayClusterItems.forEach((item) => {
                if (item.cluster_id === this.proposalData.content.cluster) {
                    cluster = item.cluster_name;
                }
            });

            return cluster;
        },

        modulesByType() {
            return this.modules.reduce((agg, curr) => {
                agg[curr.ref_module_type_id] = curr;
                return agg;
            }, {});
        },

        moduleTabList() {
            let tabs = this.proposalModules.module_types.map(tab => {
                return {
                    ...tab,
                    hasUnsavedChanges: null,
                    hasBeenActedOn: null,
                    statusIcon: false
                };
            });

            // NOTE: Puts Pathway Clusters at the top of the non-static tabs
            tabs.forEach((tab, idx) => {
                // FIXME: Hardcoded ID
                if (tab.ref_module_type_id === 57) {
                    tabs.splice(idx, 1);
                    tabs.unshift(tab);
                }
            });

            if (this.isReviewer || this.isAdmin) {
                return tabs;
            }

            return tabs.filter(tab => tab.is_agency_review !== true);
        }
    },

    async created() {
        this.activeTab = 'History';
        // NOTE: the order in which store fetches are called is important.
        this.fetchFundsAvailable(this.applicationId);

        await this.fetchApplication(this.applicationId);
        await this.fetchApplicationCosts(this.applicationId);
        await this.fetchLocalApplicationClusters(this.applicationId);

        await this.fetchProposal({ applicationId: this.applicationId, proposalId: this.proposalId });
        await this.fetchRefPerkinsIndicators({ academicYear: this.academicYear });
        if (this.isReviewer) {
            this.fetchProposalPrevious(this.proposalId);
            this.fetchProposalApproved(this.proposalId);
        }
    },

    methods: {
        ...mapActions({
            fetchApplication: 'application/fetchApplication',
            fetchFundsAvailable: 'cost/fetchFundsAvailable',
            fetchApplicationCosts: 'cost/fetchApplicationCosts',
            fetchProposal: 'proposal/fetchProposal',
            fetchLocalApplicationClusters: 'ref/application/fetchLocalApplicationClusters',
            fetchProposalPrevious: 'review/fetchProposalPrevious',
            fetchProposalApproved: 'review/fetchProposalApproved',
            fetchRefPerkinsIndicators: 'ref/perkinsIndicators/fetch',
        }),

        isActive(name) {
            return name === this.activeTab;
        },

        async selectTab(name) {
            this.activeTab = name;
            if (
                name !== this.proposalInfoTab.name &&
                name !== this.proposalHistoryTab.name
            ) {
                await this.$store.dispatch('module/activateModule', name);
            }

            let isDisabled =
                name === this.proposalInfoTab.name
                    ? this.formDisabled || Boolean(this.proposalApprovalDate)
                    : !this.isModuleEditable;

            this.$router.push({
                name: `proposal.${name}`,
                params: {
                    viewingProposal: true,
                    formDisabled: isDisabled,
                    lea: this.proposal ? this.proposal.lea : null
                }
            });
        }
    }
};
</script>

<style lang="scss">

.nav-item-status-icon {
    margin-top: -0.25rem;
    padding-left: 0.25rem;
    font-size: 1.25em;
    float: right;
}

.proposal-back-annual-plan .proposal-back-title{
  background: #380064;
  font-size: medium;
  color: white;
  text-decoration: none;
  font-weight: bold;
}

.activity-section-header {
    margin: 1em 0 0 0;
    display: flex;

    .activity-section-header-title {
        font-size: 1.05em;
        font-weight: 100;
        padding: 0 0.5em 0 0;
    }

    .header-separator {
        border-bottom: #5a6268 solid 1px;
        margin: -0.75em 2em 0.75em 0;
        min-height: 1px;
        flex: 1 1 auto;
    }

    :first-child {
        .activity-section-header {
            margin: 0 0 0 0;
        }
    }
}
.separator-line {
    border-bottom: 1px solid white;
    margin-top: .5rem;
    margin-bottom: .5rem;
}
</style>
